/**
 * THE dialog box — build standard section 4 (the look) and section 5 (closing).
 *
 * A COLUMN, NOT A SCROLL BOX. The head (title and X) and the foot (the buttons)
 * stay put and only the body scrolls, so a long form can never take its own
 * close control or its Save button off the screen. That is `.modal-scroll` in
 * theme.css; this component is the one place that always asks for it, instead
 * of each page remembering to.
 *
 * Built on the classes theme.css already carries (.modal-overlay, .modal,
 * .modal-head, .modal-body, .modal-foot), so a box made here looks like every
 * other overlay dialog on the platform and a theme change reaches it too.
 *
 * CLOSES THREE WAYS, every time: the X, Escape, and a click on the scrim.
 *   - Escape closes the TOPMOST box only, and stops there, so a page-level
 *     Escape (the HR profile panel listens on window) does not also close the
 *     page behind the box.
 *   - The scrim click can be switched off with closeOnScrim={false} for a box
 *     that must only close from its own buttons. The rota dialogs do that:
 *     Shane, 2026-08-02, "it needs to close by pressing close".
 *
 * testId goes on the overlay, as it did on the local ModalShell copies this
 * replaced, so a test that reads the box's text still finds the title in it.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// Every open Dialog, oldest first. Escape belongs to the last one.
const openStack: symbol[] = [];

export interface DialogProps {
  title: ReactNode;
  onClose: () => void;
  /** The body: the only part that scrolls. */
  children: ReactNode;
  /** The buttons, held at the bottom while the body scrolls. */
  footer?: ReactNode;
  /** On the overlay. The X gets `${testId}-close`. */
  testId?: string;
  /** Max width in px. Without it the box takes .modal's own (640). */
  width?: number;
  /** false: only the X, Escape and the box's own buttons close it. */
  closeOnScrim?: boolean;
  /** Stacking layer. Without it the box sits at .modal-overlay's own (999);
   *  a box that must be seen over anything else open asks for more. */
  zIndex?: number;
}

export function Dialog({
  title,
  onClose,
  children,
  footer,
  testId,
  width,
  closeOnScrim = true,
  zIndex,
}: DialogProps): JSX.Element {
  const { t } = useTranslation('common');
  const titleId = useId();

  // The Escape listener is registered once; the ref lets it call the latest
  // onClose without being torn down and re-pushed onto the stack each render,
  // which would move an older box above a newer one.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const me = Symbol('dialog');
    openStack.push(me);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || openStack[openStack.length - 1] !== me) return;
      e.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const at = openStack.indexOf(me);
      if (at !== -1) openStack.splice(at, 1);
    };
  }, []);

  return (
    <div
      className="modal-overlay"
      data-testid={testId}
      onClick={closeOnScrim ? onClose : undefined}
      style={{ alignItems: 'center', zIndex }}
    >
      <div
        className="modal modal-scroll pop-std dlg"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        style={width ? { maxWidth: width } : undefined}
      >
        <div className="modal-head">
          <div className="modal-title" id={titleId}>
            {title}
          </div>
          <button
            type="button"
            className="modal-close"
            aria-label={t('buttons.close')}
            onClick={onClose}
            data-testid={`${testId ?? 'dialog'}-close`}
          >
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}
